import api from "../api";
import type { User } from "../auth";
import type { Group } from "./groupService";
import discussionService from "./discussionService";

export interface Post {
  id: string;
  title?: string;
  content: string;
  author: User;
  group: Group;
  likes: number;
  comments: number;
  tags?: string[];
  attachments?: Array<{
    id: string;
    type: string;
    url: string;
    thumbnailUrl?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  content: string;
  author: User;
  post: Post;
  likes: number;
  replies?: Comment[];
  parentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostData {
  content: string;
  groupId: string;
  title?: string;
  tags?: string[];
  attachments?: File[];
}

const postService = {
  getGroupPosts: async (
    groupId: string,
    params: {
      page?: number;
      limit?: number;
      sortBy?: "recent" | "popular";
    } = {},
  ): Promise<{ posts: Post[]; total: number }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });

    const response = await api.get<{ posts: Post[]; total: number }>(
      `groups/${groupId}/posts?${queryParams.toString()}`,
    );
    return response.data;
  },

  getAllGroupPosts: async (
    params: {
      page?: number;
      limit?: number;
      sortBy?: "recent" | "popular";
    } = {},
  ): Promise<{ posts: Post[]; total: number }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });

    const response = await api.get<{ posts: Post[]; total: number }>(
      `groups/all/posts?${queryParams.toString()}`,
    );
    return response.data;
  },

  createPost: async (groupId: string, data: CreatePostData): Promise<Post> => {
    const hasAttachments = data.attachments && data.attachments.length > 0;

    if (hasAttachments) {
      const formData = new FormData();
      formData.append("content", data.content);
      if (data.title) formData.append("title", data.title);
      data.tags?.forEach((tag) => formData.append("tags[]", tag));
      data.attachments?.forEach((file) => formData.append("attachments", file));

      const response = await api.post<{ post: Post }>(
        `groups/${groupId}/posts`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.post;
    } else {
      const response = await api.post<{ post: Post }>(
        `groups/${groupId}/posts`,
        { ...data, groupId },
      );
      return response.data.post;
    }
  },

  updatePost: async (postId: string, content: string): Promise<Post> => {
    const response = await api.put<{ post: Post }>(`posts/${postId}`, {
      content,
    });
    return response.data.post;
  },

  deletePost: async (postId: string): Promise<void> => {
    await api.delete(`posts/${postId}`);
  },

  getPost: async (postId: string): Promise<Post> => {
    const response = await api.get<{ post: Post }>(`posts/${postId}`);
    return response.data.post;
  },

  likePost: async (postId: string): Promise<void> => {
    await api.post(`posts/${postId}/like`);
  },

  unlikePost: async (postId: string): Promise<void> => {
    await api.delete(`posts/${postId}/like`);
  },

  getPostComments: async (
    postId: string,
    params: {
      page?: number;
      limit?: number;
      parentId?: string;
    } = {},
  ): Promise<{ comments: Comment[]; total: number }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });

    const response = await api.get<{ comments: Comment[]; total: number }>(
      `posts/${postId}/comments?${queryParams.toString()}`,
    );
    return response.data;
  },

  createComment: async (
    postId: string,
    content: string,
    parentId?: string,
  ): Promise<Comment> => {
    const response = await api.post<{ comment: Comment }>(
      `posts/${postId}/comments`,
      { content, parentId },
    );
    return response.data.comment;
  },

  updateComment: async (
    commentId: string,
    content: string,
  ): Promise<Comment> => {
    const response = await api.put<{ comment: Comment }>(
      `comments/${commentId}`,
      { content },
    );
    return response.data.comment;
  },

  deleteComment: async (commentId: string): Promise<void> => {
    await api.delete(`comments/${commentId}`);
  },

  likeComment: async (commentId: string): Promise<void> => {
    await api.post(`comments/${commentId}/like`);
  },

  unlikeComment: async (commentId: string): Promise<void> => {
    await api.delete(`comments/${commentId}/like`);
  },

  getLatestPosts: async (params: { limit?: number } = {}): Promise<Post[]> => {
    const response = await api.get<{ posts: Post[] }>("posts/latest", {
      params,
    });
    return response.data.posts;
  },

  getTrendingPosts: async (
    params: { limit?: number } = {},
  ): Promise<Post[]> => {
    const response = await api.get<{ posts: Post[] }>("posts/trending", {
      params,
    });
    return response.data.posts;
  },

  searchDiscussions: discussionService.searchDiscussions,
};

export default postService;
